import { useState, useMemo, useEffect, useCallback } from 'react';
import { AxiosResponse } from 'axios';
import useApiFetch from '@/hooks/useApiFetch';
import candidatesServices, { Candidate } from '@/services/candidates';
import { FetchApiIds } from '@/types';
import CandidateSearchForFilterComponent, {
  AutocompleteComponentType,
} from './CandidateSearchForFilter.component';

type AutoCompleteType = Pick<AutocompleteComponentType, 'selectedOptions'> & {
  onSelectFilterOption: (value: string | number | Date) => void;
};

const CandidateSearchForFilter = ({
  selectedOptions,
  onSelectFilterOption,
}: AutoCompleteType): JSX.Element => {
  const [input, setInput] = useState('');

  const candidateSearchResponse = useApiFetch<Candidate[]>(
    [FetchApiIds.CANDIDATE_SEARCH, input],
    async () => {
      const [nameResponse, skillResponse] = await Promise.all([
        candidatesServices.searchCandidate(input),
        candidatesServices.searchCandidatesBySkill(input),
      ]);

      const nameCandidates = nameResponse.data ?? [];
      const skillCandidates = skillResponse.data ?? [];

      const candidateMap = new Map<string, Candidate>();
      [...nameCandidates, ...skillCandidates].forEach((candidate) => {
        if (!candidateMap.has(candidate.id)) {
          candidateMap.set(candidate.id, candidate);
        }
      });

      return { data: Array.from(candidateMap.values()) } as AxiosResponse<Candidate[]>;
    },
    { enabled: input.length >= 3 }
  );

  const searchedCandidates = useMemo(() => {
    return (candidateSearchResponse.data ?? []).map((candidate) => ({
      value: `${candidate.firstName} ${candidate?.lastName}`,
      node: (props) => (
        <p {...props}>
          {candidate.firstName} {candidate.lastName}
        </p>
      ),
    }));
  }, [candidateSearchResponse.data]);

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { value },
    } = event;

    setInput(value);
  };

  const onSelectItem = (value: any): any => {
    onSelectFilterOption(value);
  };

  const onPressEnter = useCallback(
    (event): void => {
      const isEnterPressed = event.key === 'Enter';
      const hasMoreThan2Characters = input?.length > 2;
      if (isEnterPressed && hasMoreThan2Characters) {
        onSelectFilterOption(input);
      }
    },
    [input, onSelectFilterOption]
  );

  useEffect(() => {
    window.addEventListener('keypress', onPressEnter);
    return () => window.removeEventListener('keypress', onPressEnter);
  }, [onPressEnter]);

  const { isFetched } = candidateSearchResponse;

  return (
    <CandidateSearchForFilterComponent
      isFetched={isFetched}
      onChangeInput={onChangeHandler}
      options={searchedCandidates}
      input={input}
      selectedOptions={selectedOptions}
      onSelectItem={onSelectItem}
    />
  );
};

export default CandidateSearchForFilter;
